import { tr, uiLocale } from "./i18n.js";
// Pure helpers for the Prospects page and Settings → Sales inbox (no React).
// Unit-tested in frontend/scripts/prospectsView.test.mjs.

export const TABS = [
  { key: "needs_reply", get label() { return tr("Needs reply"); } },
  { key: "review", get label() { return tr("Review"); } },
  { key: "waiting", get label() { return tr("Waiting on them"); } },
  { key: "clients", get label() { return tr("Clients"); } },
  { key: "won", get label() { return tr("Won"); } },
  { key: "dismissed", get label() { return tr("Dismissed"); } },
];

export const FLAG_LABELS = {
  get discount_request() { return tr("Asks for a discount"); },
  get complaint() { return tr("Complaint"); },
  get custom_deal() { return tr("Wants a custom deal"); },
  get needs_human() { return tr("Needs a person"); },
  get low_confidence() { return tr("AI unsure"); },
};

export function tabLabel(key) {
  return (TABS.find((t) => t.key === key) || {}).label || key;
}

// Same rule as backend prospects_rules.money_amounts: only amounts written
// as money ($1,500 / 1500 USD / 200 pesos); 16,000 copies is not money.
const MONEY =
  /(?:(?:US\$|USD\s?|MXN\s?|\$)\s?(\d{1,3}(?:[,\s]\d{3})+|\d+)(?:\.(\d{1,2}))?)|(?:\b(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?\s?(?:USD|MXN|dollars|d[oó]lares|pesos)\b)/gi;

export function moneyAmounts(text) {
  const out = [];
  for (const m of String(text || "").matchAll(MONEY)) {
    const whole = m[1] || m[3];
    const cents = m[1] ? m[2] : m[4];
    const n = Number(whole.replace(/[,\s]/g, "") + (cents ? `.${cents}` : ""));
    if (!Number.isNaN(n)) out.push(n);
  }
  return out;
}

export function allowedPrices(offer) {
  const s = new Set();
  for (const p of offer?.products || []) if (p.rate != null) s.add(Math.round(p.rate * 100) / 100);
  for (const p of offer?.packages || []) if (p.price != null) s.add(Math.round(p.price * 100) / 100);
  return s;
}

export function unlistedPrices(text, offer) {
  const ok = allowedPrices(offer);
  const out = [];
  for (const a of moneyAmounts(text)) {
    const r = Math.round(a * 100) / 100;
    if (!ok.has(r) && !out.includes(r)) out.push(r);
  }
  return out;
}

export function money(n) {
  if (n == null || Number.isNaN(Number(n))) return "";
  const v = Number(n);
  return `$${v.toLocaleString(uiLocale("en-US"), { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
}

// The send panel's starting choice for PublishOS: the linked client, else
// the one exact email match, else a new client filled from the draft.
export function initialClientChoice(thread, match, draft) {
  if (thread?.publishos_client_id) return { mode: "linked", client_id: thread.publishos_client_id };
  const exact = match?.exact || [];
  if (exact.length === 1) return { mode: "link", client_id: exact[0].id };
  const c = draft?.contact || {};
  return {
    mode: "create",
    fields: {
      business_name: c.business_name || "",
      contact_name: c.contact_name || thread?.prospect_name || "",
      email: thread?.prospect_email || "",
      phone: c.phone || "",
      website: c.website || "",
    },
  };
}

// What the send endpoint gets for that choice.
export function clientPayload(choice) {
  if (!choice || choice.mode === "linked") return {};
  if (choice.mode === "link") return { mode: "link", client_id: choice.client_id };
  if (choice.mode === "create") return { mode: "create", fields: { ...(choice.fields || {}) } };
  return { mode: "none" };
}

export function canCreateClient(choice) {
  if (choice?.mode !== "create") return true;
  const f = choice.fields || {};
  return Boolean((f.business_name || "").trim() || (f.contact_name || "").trim());
}

export function displayName(thread) {
  return (
    thread?.publishos_client?.business_name ||
    thread?.prospect_name ||
    thread?.prospect_email ||
    tr("Unknown sender")
  );
}

// Admin "user" filter: one entry per user who has a mailbox on this property.
export function userOptions(connections) {
  const seen = new Map();
  for (const c of connections || []) {
    if (c.user_id && !seen.has(c.user_id)) {
      seen.set(c.user_id, { id: c.user_id, label: c.user_name || c.user_email || c.address });
    }
  }
  return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
}

export function ignoreToText(list) {
  return (list || []).join("\n");
}

export function textToIgnore(text) {
  return String(text || "")
    .split(/[\s,;]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}
